import React, { useEffect, useState } from 'react';
import api from '../../../apiManager/api';
import { formatPayment } from '../../../lib/utils';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../../../components/ui/dialog";
import './StripeJobs.css';

const StripeJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [loadingJobId, setLoadingJobId] = useState(null);
    const limit = 10;

    const fetchJobs = async (currentPage) => {
        setLoading(true);
        try {
            const response = await api.get(`/job/getAllJobsNoFilter?page=${currentPage}&limit=${limit}`);
            if (response.data) {
                setJobs(response.data.jobs);
                setTotalPages(response.data.totalPages);
                setPage(response.data.currentPage);
            }
        } catch (error) {
            console.error("Error fetching jobs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs(page);
    }, [page]);

    const handleFetchStripeInfo = async (job) => {
        if (!job.paymentIntentId) {
            // Ideally show a toast here, but for now alert or log
            console.warn("No payment intent ID for job", job._id);
            return;
        }

        setLoadingJobId(job._id);
        try {
            // Backend expects paymentIntent in query params even for POST
            const response = await api.post(`/job/fetchStripeDetails?paymentIntent=${job.paymentIntentId}`);
            setSelectedPayment(response.data);
            setIsDialogOpen(true);
        } catch (error) {
            console.error("Error fetching stripe details:", error);
        } finally {
            setLoadingJobId(null);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'open': return 'status-open';
            case 'scheduled': return 'status-scheduled';
            case 'in-progress': return 'status-in-progress';
            case 'completed': return 'status-completed';
            case 'canceled': return 'status-canceled';
            case 'not-anymore': return 'status-not-anymore';
            default: return '';
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderPaymentDialog = () => {
        if (!selectedPayment) return null;

        const { id, amount, status, capture_method, amount_received, latest_charge, currency } = selectedPayment;

        // Calculations based on latest_charge
        const captured = latest_charge?.amount_captured || 0;
        const refunded = latest_charge?.amount_refunded || 0;
        const netSpent = captured - refunded;
        const remaining = amount - amount_received; // Amount yet to be received/captured

        return (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Stripe Payment Details</DialogTitle>
                        <DialogDescription>
                            Detailed information for Payment Intent: {id}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col space-y-1">
                                <span className="text-sm font-medium text-muted-foreground">Status</span>
                                <span className={`text-sm font-semibold capitalize ${status === 'succeeded' ? 'text-green-600' : 'text-yellow-600'}`}>
                                    {status}
                                </span>
                            </div>
                            <div className="flex flex-col space-y-1">
                                <span className="text-sm font-medium text-muted-foreground">Capture Method</span>
                                <span className="text-sm capitalize">{capture_method?.replace('_', ' ')}</span>
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <h4 className="mb-3 text-sm font-semibold text-gray-900">Financial Breakdown ({currency?.toUpperCase()})</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Total Authorized Amount</span>
                                    <span className="text-sm font-medium">${(amount / 100).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Amount Received</span>
                                    <span className="text-sm font-medium text-green-600">${(amount_received / 100).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Amount Captured</span>
                                    <span className="text-sm font-medium">${(captured / 100).toFixed(2)}</span>
                                </div>
                                {refunded > 0 && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-red-600">Amount Refunded</span>
                                        <span className="text-sm font-medium text-red-600">-${(refunded / 100).toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="border-t border-dashed pt-2 mt-2 flex justify-between items-center">
                                    <span className="text-sm font-bold text-gray-900">Net Spent</span>
                                    <span className="text-sm font-bold text-gray-900">${(netSpent / 100).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        {latest_charge && (
                            <div className="bg-gray-50 p-3 rounded-md text-xs text-gray-500 mt-2">
                                <p>Latest Charge ID: {latest_charge.id}</p>
                                <p>Payment Method: {latest_charge.payment_method_details?.type} - {latest_charge.payment_method_details?.card?.brand} •••• {latest_charge.payment_method_details?.card?.last4}</p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        );
    };

    return (
        <div className="stripe-jobs-container">
            <div className="stripe-jobs-header">
                <h2>Stripe Job Management</h2>
                <div className="header-pagination">
                    <button
                        className="header-page-btn"
                        disabled={page === 1}
                        onClick={() => handlePageChange(page - 1)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>
                    <span className="header-page-info">{page} / {totalPages}</span>
                    <button
                        className="header-page-btn"
                        disabled={page === totalPages}
                        onClick={() => handlePageChange(page + 1)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="loading-state">Loading jobs...</div>
            ) : (
                <div className="stripe-jobs-grid">
                    {jobs.map((job) => (
                        <div key={job._id} className="job-card">
                            <div className="job-header">
                                <span className={`job-status ${getStatusClass(job.jobStatus)}`}>
                                    {job.jobStatus}
                                </span>
                                <span className="job-id-badge">#{job._id.slice(-6)}</span>
                            </div>

                            <div className="job-section">
                                <span className="section-title">Homeowner</span>
                                <div className="user-profile">
                                    {job.houseOwnerId?.imageUrl ? (
                                        <img
                                            src={job.houseOwnerId.imageUrl}
                                            alt={job.houseOwnerId.userName}
                                            className="user-avatar"
                                        />
                                    ) : (
                                        <div className="user-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                                            {job.houseOwnerId?.userName?.charAt(0) || '?'}
                                        </div>
                                    )}
                                    <div className="user-details">
                                        <span className="user-name">{job.houseOwnerId?.userName || "Unknown User"}</span>
                                        <span className="user-id">{job.houseOwnerId?._id}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="job-section">
                                <span className="section-title">Shoveller Info</span>
                                <div className="shoveller-list">
                                    {job.ShovelerInfo && job.ShovelerInfo.length > 0 ? (
                                        job.ShovelerInfo.map((info, index) => {
                                            const shoveller = info.ShovelerId || {};
                                            return (
                                                <div key={index} className="shoveller-item">
                                                    <div className="shoveller-details">
                                                        <div className="shoveller-name">
                                                            {shoveller.firstName} {shoveller.lastName}
                                                        </div>
                                                        <div className="shoveller-email">{shoveller.email}</div>
                                                        <div className="shoveller-id">ID: {shoveller._id || info.ShovelerId}</div>
                                                    </div>
                                                    <div className="shoveller-status-badge">
                                                        {info.shovellerAction}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <span style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                                            No shovellers assigned
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="job-section">
                                <span className="section-title">Payment Details</span>
                                <div className="payment-grid">
                                    <div className="payment-row">
                                        <span className="payment-label">Original Payment</span>
                                        <span className="payment-value">${formatPayment(job.paymentInfo?.amount || 0, false)}</span>
                                    </div>
                                    <div className="payment-row">
                                        <span className="payment-label">Helper Payment</span>
                                        <span className="payment-value highlight">${formatPayment(job.paymentInfo?.amount || 0, true)}</span>
                                    </div>
                                    <div className="payment-row">
                                        <span className="payment-label">Initialization</span>
                                        <span className="payment-value capitalize">{job.paymentInfo?.initialization || 'None'}</span>
                                    </div>
                                    <div className="payment-row">
                                        <span className="payment-label">Status</span>
                                        <span className="payment-value capitalize status-text">{job.paymentInfo?.status || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: 'auto', textAlign: 'right' }}>
                                {formatDate(job.createdAt)}
                            </div>

                            <button
                                className="fetch-btn"
                                onClick={() => handleFetchStripeInfo(job)}
                                disabled={loadingJobId === job._id || !job.paymentIntentId}
                                style={{ opacity: !job.paymentIntentId ? 0.5 : 1 }}
                            >
                                <span>{loadingJobId === job._id ? 'Fetching...' : 'Fetch Stripe Info'}</span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                                    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                                    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {renderPaymentDialog()}
        </div>
    );
};

export default StripeJobs;
